import { useTranslation } from 'react-i18next';
import {
  LAUNCHER_LABELS,
  LAUNCH_LINK_EXAMPLES,
  type DeviceDto,
  type DevicePathDto,
} from '@gc/shared';

import { inputClass } from './controls';
import { normalizeLaunchTarget } from '@gc/shared';

import { payloadDeviceIds, presentWhileTyping } from './launchTargetPresenter';

/**
 * Default path-or-link + per-device overrides (SPEC §6.6, §6.7).
 *
 * The spec is explicit that the same title is NOT installed at the same path on
 * every PC, so the default is a fallback and each device may override it.
 * Launch resolves this device's override first, then the default — the shared
 * `resolveExecutablePath` helper is the single implementation of that rule.
 *
 * Since 2026-09-09 the field takes a LINK as well as a path (owner): a title
 * the venue installed itself is the .exe on the PC; a title that lives in
 * Steam, the Microsoft Store, Epic, Riot, … is its launch link. The shared
 * `parseLaunchTarget` says which it is, and a line under the field says so
 * as it is typed — "Steam · 730", "Program on this PC", or the problem.
 *
 * A blank override row means "no override": it is simply not sent, so the
 * device falls back to the default rather than storing an empty path.
 */
export function DevicePathEditor({
  devices,
  defaultPath,
  onDefaultPathChange,
  overrides,
  onOverridesChange,
  defaultPathError,
  overrideErrors,
}: {
  devices: DeviceDto[];
  defaultPath: string;
  onDefaultPathChange: (value: string) => void;
  overrides: Record<string, string>;
  onOverridesChange: (next: Record<string, string>) => void;
  /** Existing rows, so the agent's detect-scan result can be shown. */
  detected?: DevicePathDto[];
  /** A refusal filed against the default field (pre-submit or from the API). */
  defaultPathError?: string;
  /** Refusals per device id, for the override rows. */
  overrideErrors?: Record<string, string>;
}) {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <label className="block">
        <span className="mb-1 block text-sm text-content-secondary">
          {t('settings.paths.pathOrLink')}
        </span>
        <input
          value={defaultPath}
          onChange={(event) => onDefaultPathChange(event.target.value)}
          // A slip in the punctuation (`steam//:…`) is put right the moment
          // the field is left, so what is saved is what the PC will open.
          onBlur={() => onDefaultPathChange(normalizeLaunchTarget(defaultPath))}
          dir="ltr"
          placeholder={t('settings.paths.pathOrLinkPlaceholder')}
          className={inputClass}
        />
        <RecognisedLine value={defaultPath} error={defaultPathError} />
        <span className="mt-1 block text-xs text-content-muted">
          {t('settings.paths.defaultPathHint')}
        </span>
      </label>

      <LaunchTargetHelp />

      <div>
        <span className="mb-1 block text-sm text-content-secondary">
          {t('settings.paths.perDeviceOverrides')}
        </span>

        {devices.length === 0 ? (
          <p className="text-sm text-content-muted">{t('settings.paths.noDevices')}</p>
        ) : (
          <div className="max-h-64 space-y-2 overflow-auto rounded-control border border-subtle bg-input p-2">
            {devices.map((device) => (
              <div key={device.id} data-override-row={device.id}>
                <label className="flex items-center gap-2">
                  <span className="w-24 shrink-0 truncate text-xs text-content-secondary">
                    {device.name}
                  </span>
                  <input
                    value={overrides[device.id] ?? ''}
                    onChange={(event) =>
                      onOverridesChange({ ...overrides, [device.id]: event.target.value })
                    }
                    onBlur={() =>
                      onOverridesChange({
                        ...overrides,
                        [device.id]: normalizeLaunchTarget(overrides[device.id] ?? ''),
                      })
                    }
                    dir="ltr"
                    placeholder={t('settings.paths.usesDefault')}
                    className={`${inputClass} flex-1 py-1 text-xs`}
                  />
                </label>
                <RecognisedLine
                  value={overrides[device.id] ?? ''}
                  error={overrideErrors?.[device.id]}
                  compact
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * The line under a field: a server or pre-submit refusal wins; otherwise
 * what the parser made of the value ("Steam · 730" / "Program on this PC" /
 * the problem sentence, in red); nothing at all for a blank field.
 */
function RecognisedLine({
  value,
  error,
  compact,
}: {
  value: string;
  error?: string | undefined;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const size = compact ? 'ms-[6.5rem] text-[11px]' : 'text-xs';

  if (error) {
    return (
      <span data-launch-target="error" className={`mt-1 block ${size} text-danger`}>
        {error}
      </span>
    );
  }

  const shown = presentWhileTyping(value);
  if (shown.tone === 'none') return null;
  if (shown.tone === 'problem') {
    return (
      <span data-launch-target="problem" className={`mt-1 block ${size} text-danger`}>
        {t(shown.key)}
      </span>
    );
  }
  return (
    <span
      data-launch-target="ok"
      dir="ltr"
      className={`mt-1 block ${size} text-content-secondary ${compact ? '' : 'text-start'}`}
    >
      {t(shown.key, shown.params ?? {})}
    </span>
  );
}

/**
 * "What can I enter?" — collapsed by default so the drawer stays short, one
 * example per launcher straight from the shared list so a launcher added
 * to the parser appears here without a second edit.
 */
function LaunchTargetHelp() {
  const { t } = useTranslation();
  return (
    <details className="rounded-control border border-subtle bg-input px-3 py-2 text-xs">
      <summary className="cursor-pointer text-content-secondary">
        {t('settings.paths.help.toggle')}
      </summary>
      <div className="mt-2 space-y-2 text-content-muted">
        <p>{t('settings.paths.help.file')}</p>
        <p>{t('settings.paths.help.link')}</p>
        <ul className="space-y-0.5">
          {LAUNCH_LINK_EXAMPLES.map(({ launcher, example }) => (
            <li key={launcher} className="flex flex-wrap gap-x-2">
              <span className="shrink-0 text-content-secondary">{LAUNCHER_LABELS[launcher]}</span>
              <code dir="ltr" className="break-all text-content">
                {example}
              </code>
            </li>
          ))}
        </ul>
        <p>{t('settings.paths.help.shortcutUrl')}</p>
        <p>{t('settings.paths.help.storePages')}</p>
        <p>{t('settings.paths.help.riot')}</p>
      </div>
    </details>
  );
}

/**
 * Turns the override map into the array the API expects, dropping blanks.
 *
 * The order is `payloadDeviceIds`' — the same order `mapDevicePathFieldErrors`
 * reads the API's `devicePaths.<index>.path` refusals back through.
 *
 * `installed: true` is stated rather than left to the schema default: a path an
 * operator typed by hand is asserted to exist. The agent's detect scan is what
 * later flips this to false for a machine where the title is actually missing.
 */
export function toDevicePathPayload(
  overrides: Record<string, string>,
): { deviceId: string; path: string; installed: boolean }[] {
  return payloadDeviceIds(overrides).map((deviceId) => ({
    deviceId,
    path: (overrides[deviceId] ?? '').trim(),
    installed: true,
  }));
}

/** Existing override rows -> the editable map. */
export function toOverrideMap(paths: readonly DevicePathDto[]): Record<string, string> {
  return Object.fromEntries(paths.map((entry) => [entry.deviceId, entry.path]));
}
